import React, { useState } from 'react';
import {
    ActionIcon,
    Button,
    Group,
    Modal,
    Paper,
    Stack,
    Text,
    ThemeIcon,
} from '@mantine/core';
import { IconBox, IconLayout, IconPlus, IconTrash } from '@tabler/icons-react';
import useSWR, { useSWRConfig } from 'swr';
import { notifications } from '@mantine/notifications';
import {
    deleteComponent,
    deleteScene,
    listComponents,
    listScenes,
    type ShellComponent,
    type Scene,
} from '../../../data/ShellConfig';
import { useCsrf } from '../../../hooks/useCsrf';
import { track } from '../../../utils/mixpanel';
import { AnalyticsEvents } from '../../../analytics/events';
import StudioDrawer from './StudioDrawer';
import NewComponentDrawer from './NewComponentDrawer';
import NewSceneDrawer from './NewSceneDrawer';

export type LibraryKind = 'components' | 'scenes';

type LibraryDrawerProps = {
    kind: LibraryKind;
    opened: boolean;
    onClose: () => void;
};

type LibraryRow = { id: string; name: string; usage: string };

const componentRow = (component: ShellComponent): LibraryRow => ({
    id: component.id,
    name: component.name,
    usage: `Used by ${component.scenesCount ?? 0} scene${component.scenesCount === 1 ? '' : 's'}`,
});

const sceneRow = (scene: Scene): LibraryRow => ({
    id: scene.id,
    name: scene.name,
    usage: `Used by ${scene.playlistsCount ?? 0} playlist${scene.playlistsCount === 1 ? '' : 's'}`,
});

/**
 * The Library drill-in: a list of components or scenes with usage counts, plus
 * create, edit, and delete. Tapping anywhere on a row opens its editor; the
 * delete icon is exempt. Opens as a right drawer on desktop, full-screen on
 * mobile.
 */
const LibraryDrawer: React.FC<LibraryDrawerProps> = ({ kind, opened, onClose }) => {
    const { csrfToken } = useCsrf();
    const { mutate } = useSWRConfig();
    const isComponents = kind === 'components';
    const listKey = isComponents ? 'api/components' : 'api/scenes';

    const { data: components = [] } = useSWR('api/components', listComponents);
    const { data: scenes = [] } = useSWR('api/scenes', listScenes);

    const [createOpen, setCreateOpen] = useState(false);
    const [editComponentId, setEditComponentId] = useState<string | null>(null);
    const [editSceneId, setEditSceneId] = useState<string | null>(null);
    const [toDelete, setToDelete] = useState<LibraryRow | null>(null);

    const openEditor = (id: string) =>
        isComponents ? setEditComponentId(id) : setEditSceneId(id);

    const rows: LibraryRow[] = isComponents
        ? components.map(componentRow)
        : scenes.map(sceneRow);

    const revalidate = () => mutate(listKey);

    const handleDelete = async () => {
        if (!toDelete) return;
        try {
            if (isComponents) {
                await deleteComponent(toDelete.id, csrfToken);
                track(AnalyticsEvents.ComponentDeleted, { componentId: toDelete.id });
            } else {
                await deleteScene(toDelete.id, csrfToken);
                track(AnalyticsEvents.SceneDeleted, { sceneId: toDelete.id });
            }
            // A deleted component/scene ripples into scenes and playlists.
            await Promise.all([revalidate(), mutate('api/scenes'), mutate('api/playlists')]);
            notifications.show({ message: `Deleted “${toDelete.name}”.` });
        } catch {
            notifications.show({ color: 'red', message: `Could not delete “${toDelete.name}”.` });
        }
        setToDelete(null);
    };

    return (
        <StudioDrawer
            opened={opened}
            onClose={onClose}
            onBack={onClose}
            title={isComponents ? 'Components' : 'Scenes'}
        >
            <Stack gap="md">
                <Button
                    fullWidth
                    leftSection={<IconPlus size={16} />}
                    onClick={() => setCreateOpen(true)}
                >
                    {isComponents ? 'New component' : 'New scene'}
                </Button>

                {rows.length === 0 ? (
                    <Text c="dimmed" size="sm" ta="center" py="lg">
                        No {kind} yet.
                    </Text>
                ) : (
                    <Stack gap="xs">
                        {rows.map(row => (
                            <Paper
                                key={row.id}
                                withBorder
                                radius="md"
                                p="sm"
                                onClick={() => openEditor(row.id)}
                                style={{ cursor: 'pointer' }}
                            >
                                <Group justify="space-between" wrap="nowrap">
                                    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                                        <ThemeIcon variant="light" color="gray" radius="md">
                                            {isComponents ? (
                                                <IconBox size={18} />
                                            ) : (
                                                <IconLayout size={18} />
                                            )}
                                        </ThemeIcon>
                                        <div style={{ minWidth: 0 }}>
                                            <Text fw={600} lineClamp={1}>
                                                {row.name}
                                            </Text>
                                            <Text size="xs" c="dimmed">
                                                {row.usage}
                                            </Text>
                                        </div>
                                    </Group>
                                    <ActionIcon
                                        variant="subtle"
                                        color="red"
                                        aria-label={`Delete ${row.name}`}
                                        onClick={event => {
                                            // Don't let the delete tap open the editor.
                                            event.stopPropagation();
                                            setToDelete(row);
                                        }}
                                    >
                                        <IconTrash size={16} />
                                    </ActionIcon>
                                </Group>
                            </Paper>
                        ))}
                    </Stack>
                )}
            </Stack>

            {isComponents ? (
                <>
                    <NewComponentDrawer
                        opened={createOpen}
                        onClose={() => setCreateOpen(false)}
                        onSaved={() => void revalidate()}
                    />
                    <NewComponentDrawer
                        opened={editComponentId !== null}
                        componentId={editComponentId ?? undefined}
                        onClose={() => setEditComponentId(null)}
                        onSaved={() => void revalidate()}
                    />
                </>
            ) : (
                <>
                    <NewSceneDrawer
                        opened={createOpen}
                        onClose={() => setCreateOpen(false)}
                        components={components}
                        onSaved={() => {
                            void revalidate();
                            setCreateOpen(false);
                        }}
                    />
                    <NewSceneDrawer
                        opened={editSceneId !== null}
                        sceneId={editSceneId ?? undefined}
                        onClose={() => setEditSceneId(null)}
                        components={components}
                        onSaved={() => {
                            // A scene edit ripples into the playlists that use it.
                            void Promise.all([revalidate(), mutate('api/playlists')]);
                            setEditSceneId(null);
                        }}
                    />
                </>
            )}

            <Modal
                opened={toDelete !== null}
                onClose={() => setToDelete(null)}
                title={isComponents ? 'Delete component' : 'Delete scene'}
                centered
            >
                <Text size="sm" mb="lg">
                    Delete “{toDelete?.name}”?{' '}
                    {isComponents
                        ? 'Scenes using it will show an empty panel until you pick another.'
                        : 'It will be removed from any playlists that use it.'}
                </Text>
                <Group justify="flex-end">
                    <Button variant="default" onClick={() => setToDelete(null)}>
                        Cancel
                    </Button>
                    <Button color="red" onClick={handleDelete}>
                        Delete
                    </Button>
                </Group>
            </Modal>
        </StudioDrawer>
    );
};

export default LibraryDrawer;
